import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { Checkbox } from "@fluentui/react/lib/Checkbox";
import { Dialog, DialogFooter, DialogType } from "@fluentui/react/lib/Dialog";
import { Modal } from "@fluentui/react/lib/Modal";
import { ScrollablePane } from "@fluentui/react/lib/ScrollablePane";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { Stack } from "@fluentui/react/lib/Stack";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { useCallback, useState, useEffect } from "react";

const styles = mergeStyleSets({
  dialogContent: {
    height: "250px",
    minHeight: "100px",
    maxHeight: "600px",
    position: "relative"
  },
  listItem: {
    padding: "10px 0"
  },
  modal: {
    main: {
      maxWidth: "700px",
      minWidth: "400px"
    }
  }
});

interface ModalItem {
  id: string | number;
  text: string;
  preSelected?: boolean;
  required?: boolean;
}

interface GenericModalProps {
  title: string;
  items: ModalItem[];
  onAction: (selectedIds: (string | number)[]) => void;
  actionButtonText?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  loadingText?: string;
}

export function ModalListItens({
  title,
  items,
  onAction,
  actionButtonText = "Confirmar",
  isOpen,
  onOpenChange,
  isLoading = false,
  loadingText = "Processando..."
}: GenericModalProps) {
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(currentSelectedIds => {
        const validItemIds = new Set(items.map(item => item.id));
        const newSelectedSet = new Set(
          currentSelectedIds.filter(id => validItemIds.has(id))
        );

        items.forEach(item => {
          if (item.preSelected || item.required) {
            newSelectedSet.add(item.id);
          }
        });

        return Array.from(newSelectedSet);
      });
    }
  }, [isOpen, items]);

  const handleAction = useCallback(() => {
    onAction(selectedIds);
    setSelectedIds([]);
  }, [onAction, selectedIds]);

  const handleCancel = useCallback(() => {
    setSelectedIds([]);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleCheckboxChange = useCallback(
    (itemId: string | number, checked?: boolean) => {
      const item = items.find(i => i.id === itemId);

      if (!item) return;

      if (item.required && !checked) {
        return;
      }

      if (checked) {
        setSelectedIds(prev => [...new Set([...prev, itemId])]);
      } else {
        setSelectedIds(prev => prev.filter(id => id !== itemId));
      }
    },
    [items]
  );

  const handleSelectAll = useCallback(() => {
    const allSelectable = items.map(item => item.id);
    setSelectedIds(allSelectable);
  }, [items]);

  const handleDeselectAll = useCallback(() => {
    const requiredItems = items
      .filter(item => item.required)
      .map(item => item.id);
    setSelectedIds(requiredItems);
  }, [items]);

  const requiredIds = items.filter(i => i.required).map(i => i.id);
  const allSelectableIds = items.map(i => i.id);

  const areAllSelected = allSelectableIds.every(id => selectedIds.includes(id));

  const hasPartialSelection =
    selectedIds.length > requiredIds.length && !areAllSelected;

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleCancel}
      isBlocking={false}
      containerClassName={styles.modal}
    >
      <Dialog
        hidden={!isOpen}
        onDismiss={handleCancel}
        dialogContentProps={{
          type: DialogType.normal,
          title: title
        }}
        modalProps={{
          isBlocking: false
        }}
      >
        <div className={styles.dialogContent}>
          <div style={{ position: "relative", height: "100%" }}>
            <ScrollablePane>
              <Stack>
                {items.map(item => (
                  <div key={item.id} className={styles.listItem}>
                    <Checkbox
                      label={item.required ? `${item.text} *` : item.text}
                      checked={selectedIds.includes(item.id)}
                      onChange={(ev, checked) =>
                        handleCheckboxChange(item.id, checked)
                      }
                    />
                  </div>
                ))}
              </Stack>
            </ScrollablePane>
          </div>
        </div>

        <div
          style={{
            marginTop: "20px",
            textAlign: "center",
            paddingTop: "20px",
            borderTop: "1px solid #e0e0e0"
          }}
        >
          <Checkbox
            label="Selecionar todos"
            checked={areAllSelected}
            indeterminate={hasPartialSelection}
            onChange={() => {
              if (areAllSelected) {
                handleDeselectAll();
              } else {
                handleSelectAll();
              }
            }}
          />

          {items.some(item => item?.required) && (
            <div
              style={{
                fontSize: "12px",
                marginTop: "8px",
                color: "#666",
                fontStyle: "italic"
              }}
            >
              * Itens obrigatórios não podem ser desmarcados
            </div>
          )}
        </div>
        <DialogFooter>
          <DefaultButton onClick={handleCancel} disabled={isLoading}>
            Cancelar
          </DefaultButton>
          <PrimaryButton
            onClick={handleAction}
            disabled={selectedIds.length === 0 || isLoading}
          >
            {isLoading && (
              <Spinner
                size={SpinnerSize.small}
                style={{ marginRight: "8px" }}
              />
            )}
            {isLoading ? loadingText : actionButtonText}
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
